import { useState, useRef, useCallback } from "react";

export function useSpeechRecognition() {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript]   = useState("");
  const [error, setError]             = useState(null);

  const recognitionRef       = useRef(null);
  const sessionTranscriptRef = useRef("");
  const baseTranscriptRef    = useRef("");

  const isSupported =
    "SpeechRecognition" in window || "webkitSpeechRecognition" in window;

  const startListening = useCallback(() => {
    if (!isSupported) {
      setError("Speech recognition is not supported. Please use Chrome or Safari.");
      return;
    }
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    sessionTranscriptRef.current = "";
    setError(null);

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous      = true;
    recognition.interimResults  = false;
    recognition.lang            = "en-US";
    recognition.maxAlternatives = 1;

    recognition.onstart = () => { setIsListening(true); };

    recognition.onresult = (event) => {
      let newText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          newText += event.results[i][0].transcript + " ";
        }
      }
      if (!newText.trim()) return;
      sessionTranscriptRef.current = (sessionTranscriptRef.current + " " + newText).trim();
      const full = baseTranscriptRef.current
        ? baseTranscriptRef.current + " " + sessionTranscriptRef.current
        : sessionTranscriptRef.current;
      setTranscript(full.trim());
    };

    recognition.onerror = (event) => {
      if (event.error === "not-allowed") { setError("Microphone access denied. Please allow permissions."); setIsListening(false); return; }
      if (event.error === "audio-capture") { setError("No microphone detected."); setIsListening(false); return; }
      if (event.error === "network") { setError("Network error. Please try again."); setIsListening(false); return; }
      if (event.error === "no-speech") { return; }
      setError("Recording error: " + event.error + ". Please try again.");
      setIsListening(false);
    };

    recognition.onend = () => { setIsListening(false); recognitionRef.current = null; };

    recognitionRef.current = recognition;
    try { recognition.start(); }
    catch (err) { setError("Failed to start speech recognition."); setIsListening(false); }
  }, [isSupported]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      const b = baseTranscriptRef.current;
      const s = sessionTranscriptRef.current;
      baseTranscriptRef.current = b ? (b + " " + s).trim() : s.trim();
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const clearTranscript = useCallback(() => {
    if (recognitionRef.current) { recognitionRef.current.onend = null; recognitionRef.current.stop(); recognitionRef.current = null; }
    sessionTranscriptRef.current = "";
    baseTranscriptRef.current    = "";
    setTranscript("");
    setError(null);
    setIsListening(false);
  }, []);

  return { isListening, transcript, error, isSupported, startListening, stopListening, clearTranscript };
}
